/**
 * Searches within an oversized collection without leaving the collection page.
 *
 * Collections above 5,000 products get no facets from Shopify, so this fetches
 * the /search section (scoped by a product filter carried in the form's hidden
 * inputs) and swaps its filters, results and toolbar into the collection grid.
 * The URL stays on /collections/<handle>.
 *
 * Once the facets have been injected, <facet-filters> owns every subsequent
 * request: it rebuilds each one from the #facets form, which carries the query
 * term and the active scope filter forward automatically.
 */
if (!customElements.get('collection-search-scope')) {
  // The device picker writes this filter; the search box and the picker both
  // have to respect it, so it is named once here.
  const DEVICE_FILTER = 'filter.p.m.custom.compability';

  // Where the search box sits inside the filter panel, and the width at which it
  // goes there. 1024 is the breakpoint the sidebar itself flips on - below it the
  // panel is a slide-in drawer behind the Filter button, and a search box hidden
  // in there would be worse than one above the grid.
  const SEARCH_SLOT = '[data-search-slot]';

  // Local mode indexes the collection's own product list. 250 is the maximum
  // products.json will return per request, and 8 pages caps a collection at
  // 2,000 products - beyond that the index costs too many requests to build on
  // a storefront, and the collection wants a search_scope metafield instead.
  const LOCAL_PAGE_SIZE = 250;
  const LOCAL_MAX_PAGES = 8;
  const LOCAL_MAX_PRODUCTS = LOCAL_PAGE_SIZE * LOCAL_MAX_PAGES;
  const LOCAL_DEBOUNCE = 250;

  // Results are the theme's own cards, pulled from the collection's section
  // render a page at a time, and paged the same way the collection pages.
  // LOCAL_GRID_PAGE is only the first guess at that page size - the real one is
  // learned from the first response.
  const LOCAL_GRID_PAGE = 36;

  // The facet feed pages at Liquid's own maximum; the cap matches the index's,
  // so the two describe the same stretch of the collection.
  const FACET_VIEW_PAGE = 50;
  const FACET_MAX_PAGES = Math.ceil(LOCAL_MAX_PRODUCTS / FACET_VIEW_PAGE);

  // How long the grid may stay masked without a search landing to lift it.
  const MASK_TIMEOUT = 6000;

  // Shopify's search needs a term: an empty one returns nothing, and a filter
  // alone does not narrow an oversized collection at all. The device name stands
  // in whenever the shopper has not typed their own words. It is never shown in
  // the box and never written to the address bar.
  //
  // It has to be the device name rather than a wildcard. Shopify returns NO
  // facets for a search matching over 1,000 products, and a wildcard matches the
  // whole collection - which is why the filter panel came back empty. The device
  // name is narrow by construction ("Xiaomi 15 Ultra" matches ~45 products), so
  // the panel comes back populated and its counts describe the results shown.

  class CollectionSearchScope extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.js-collection-search-form');
      this.input = this.querySelector('input[name="q"]');
      this.grid = document.querySelector('custom-pagination.main-products-grid');
      this.results = document.getElementById('filter-results');
      this.toolbar = document.getElementById('products-toolbar');
      this.pristine = null;
      this.gridAttrs = null;

      // The section masks the grid while a ?q= url loads; bailing out must lift
      // it, or the page sits blank until the mask's fallback timer.
      const unmask = () => document.documentElement.classList.remove('collection-search-loading');

      if (!this.form || !this.input || !this.grid || !this.results) return unmask();

      this.mode = this.dataset.mode === 'local' ? 'local' : 'scoped';

      // Local mode has to read the whole collection to search it exactly. Past
      // the cap that is too many requests, so the box is withdrawn rather than
      // left searching a silently truncated index.
      if (this.mode === 'local' && Number(this.dataset.collectionCount || 0) > LOCAL_MAX_PRODUCTS) {
        this.hidden = true;
        return unmask();
      }

      // Where the box lives when it is not in the sidebar. Remembered so it can
      // always be put back - at a narrow width, and before each panel swap.
      this.formHome = this.form.parentNode;
      this.formAnchor = this.form.nextSibling;

      this.form.addEventListener('submit', this.handleSubmit.bind(this));

      // Local mode answers from an in-memory index, so it can filter as the
      // shopper types rather than making them submit.
      if (this.mode === 'local') {
        this.input.addEventListener('input', () => {
          clearTimeout(this.localTimer);
          // A new term starts at page one, whatever page the shopper was on.
          this.localPageWanted = 1;
          this.localTimer = setTimeout(() => this.runLocal(), LOCAL_DEBOUNCE);
        });
      }

      // Fires when the input's native "clear" button is used.
      this.input.addEventListener('search', () => {
        if (this.input.value.trim()) return;

        // Scoped mode only searches on submit, so words typed and never
        // submitted leave nothing to clear. Restoring then put back a panel
        // cached at some earlier search, ticks and all, under a bare url.
        if (this.mode !== 'local' && !new URLSearchParams(window.location.search).has('q')) return;

        if (this.mode === 'local') {
          this.restore();
          return;
        }

        // Clearing the words must not clear the device: fall back to the
        // device-only view rather than the whole collection.
        if (this.activeDevice()) {
          this.fetchAndSwap(this.buildParams());
        } else {
          this.restore();
        }
      });

      // The theme hides the native clear button, so the box brings its own and
      // routes it through the handler above. A keystroke run still waiting on
      // its debounce is cancelled so it does not restore a second time.
      this.querySelector('.collection-search-box__clear')?.addEventListener('click', () => {
        clearTimeout(this.localTimer);
        this.input.value = '';
        this.input.focus({ preventScroll: true });
        this.input.dispatchEvent(new Event('search'));
      });

      this.results.addEventListener('click', this.handleResultsClick.bind(this));

      // facet-filters.js re-renders the panel through its own request, so the
      // corrections have to be re-applied every time it lands, not only after
      // this element's own swap.
      document.addEventListener('on:facet-filters:updated', () => {
        // Every panel render, this element's and facet-filters.js's alike, can
        // land a fresh slot - so the box is re-seated on all of them, not only
        // after the swap below.
        this.mountSearchBox();

        // A facet click goes through facet-filters.js, which re-renders the
        // grid from the collection and knows nothing about the term - so the
        // search has to be laid back over the result it just landed. This is
        // the "filter while searching" direction; runLocal covers the other.
        // Keyed on the box, not on localTerm: localTerm is only set once a run
        // renders, so a filter render landing while the first run was still
        // loading re-applied nothing and left plain filter results on screen.
        // The run counter makes the older run stand down.
        if (this.mode === 'local' && this.input.value.trim()) {
          clearTimeout(this.localTimer);
          this.localPageWanted = 1;
          this.scrollOnNextRender = true;
          this.runLocal();
        }

        if (this.dataset.noNativeFilters !== 'true') return;
        this.dropDeviceFacet();
        this.stripFilterCounts();
      });

      // "Clear all" means everything, the search included. On a scoped
      // collection Shopify builds its href from the stand-in term's results, so
      // the reload to the bare collection is the answer there.
      this.grid.addEventListener('click', (evt) => {
        if (!evt.target.closest('.js-clear-all')) return;
        // Local mode handles this through handleFilterLink, which also empties
        // the box before the unfiltered collection renders.
        if (this.mode === 'local') return;
        evt.preventDefault();
        window.location = window.location.pathname;
      });
      // Anything that re-renders the grid from the collection - a facet tick, an
      // applied-filter chip, "clear", the price range, sort - lands a view that
      // knows nothing about the search, and that view is the whole collection.
      // Masking it has to catch every one of those, so rather than listening for
      // the input that started it, watch for the state they all pass through:
      // facet-filters.js marks the results as loading before it swaps them.
      // The applied-filter chips and the clear links are ordinary anchors, and
      // Shopify builds their hrefs from the facets alone - no `q`, because the
      // term never enters that form in local mode. Following one therefore
      // navigates to a url with the search stripped out, which is why the X on a
      // chip appeared to do nothing. Handled here instead: the facet change is
      // applied in place and the term survives.
      document.addEventListener('click', this.handleFilterLink.bind(this), true);

      this.watchForFacetRender();

      window.addEventListener('popstate', this.handleHistoryChange.bind(this));

      // A collection with native filters already has the panel on the page, so
      // the box moves in immediately; an oversized one has none until the first
      // search injects it, and the swap re-seats the box then.
      this.mountSearchBox();

      this.bootstrap();
    }

    /**
     * Applies an applied-filter chip or a clear link without leaving the page.
     *
     * Only while a local search is running - otherwise the theme's own handling
     * is correct and is left alone.
     * @param {MouseEvent} evt - The click.
     */
    handleFilterLink(evt) {
      if (this.mode !== 'local' || !this.input.value.trim()) return;
      if (evt.defaultPrevented || evt.button !== 0 || evt.metaKey || evt.ctrlKey) return;

      const link = evt.target.closest
        ? evt.target.closest('.facets__active-filters a, .js-clear-filter, .js-clear-all')
        : null;
      if (!link || !link.href) return;

      const facets = document.querySelector('facet-filters');
      if (!facets || !facets.applyFilters) return;

      // Stop both the navigation and the theme's own handler, which would
      // rebuild the same request a second time.
      evt.preventDefault();
      evt.stopPropagation();

      // The href carries the facets that should remain. The term is re-applied
      // by the on:facet-filters:updated handler once this lands - except for
      // "Clear all", which clears the search along with the filters. Emptied
      // before the request, so neither the mask nor the re-apply fires.
      const params = new URL(link.href, window.location.origin).searchParams;
      params.delete('q');

      if (link.matches('.js-clear-all')) {
        // A keystroke's run still waiting on the debounce would otherwise fire
        // into the empty box, call restore() while the url still holds the old
        // filters, and put them straight back.
        clearTimeout(this.localTimer);
        this.localRun = (this.localRun || 0) + 1;
        this.input.value = '';
        this.localTerm = '';
        this.localPageWanted = 1;
        this.grid.classList.remove('is-local-search', 'is-search-pending', 'is-facets-narrowed');
      }

      this.scrollOnNextRender = true;
      facets.applyFilters(params.toString(), null, true);
    }

    /**
     * Brings the top of the results into view after a filter change, so the
     * shopper is looking at the results they just changed rather than wherever
     * they happened to be scrolled to.
     */
    scrollToResults() {
      customElements.get('products-toolbar')?.scrollToResults();
    }

    /**
     * Masks the grid whenever something else re-renders it during a search.
     *
     * facet-filters.js puts `is-loading` on the results the moment it starts
     * fetching, and takes it off once the new markup is in - so the mask goes on
     * with the class and comes off in renderLocal, once the search has been laid
     * back over whatever landed. Watching the class rather than the click is
     * what makes this cover the chips and "clear" links too, which never fire an
     * input event at all.
     */
    watchForFacetRender() {
      if (this.renderWatcher) return;

      this.renderWatcher = new MutationObserver(() => {
        if (this.mode !== 'local' || !this.input.value.trim()) return;
        if (!this.results.classList.contains('is-loading')) return;

        // Deliberately unconditional while a term is active. An earlier version
        // skipped this whenever one of our own runs was in flight, to avoid
        // masking a plain search - but a facet re-render always has such a run
        // in flight, since it is what starts it, so the mask was refused in the
        // one case it exists for. Masking a plain search costs nothing: the
        // grid it hides is the previous search's, already stale.
        this.grid.classList.add('is-search-pending');

        // The mask is lifted by runLocal, which always runs its finally. This
        // is the belt and braces for a render that never reaches one.
        clearTimeout(this.maskTimer);
        this.maskTimer = setTimeout(() => {
          this.grid.classList.remove('is-search-pending');
        }, MASK_TIMEOUT);
      });

      this.renderWatcher.observe(this.results, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    /**
     * Moves the search box into the filter panel - the sidebar at lg+, the
     * filter drawer below it - or back to its own section when there is none.
     *
     * The box is moved, never copied. It carries the hidden `filter.*` inputs
     * that scope every request to this collection, and those cannot be rendered
     * inside the panel instead: the panel is fetched from /search, where
     * `collection` is nil, so the search_scope metafield reads blank and the box
     * would quietly search the whole catalogue. Moving the live node also keeps
     * the shopper's typed term and the element's own listeners intact.
     */
    mountSearchBox() {
      if (!this.form || !this.formHome) return;

      // Only local mode moves in, at every width. The scoped search template
      // serves the huge collections, where the box stays above the grid.
      const slot = this.mode === 'local'
        ? this.grid.querySelector(`.main-products-grid__filters ${SEARCH_SLOT}`)
        : null;

      if (slot) {
        if (this.form.parentNode === slot) return;
        this.withFocusKept(() => {
          slot.append(this.form);
          slot.hidden = false;
        });
        return;
      }

      // Leave no empty slot holding its own spacing behind in the sidebar.
      const emptied = this.grid.querySelector(`.main-products-grid__filters ${SEARCH_SLOT}`);
      if (emptied && !emptied.contains(this.form)) emptied.hidden = true;

      if (this.form.parentNode === this.formHome) return;
      this.withFocusKept(() => {
        if (this.formAnchor && this.formAnchor.parentNode === this.formHome) {
          this.formHome.insertBefore(this.form, this.formAnchor);
        } else {
          this.formHome.append(this.form);
        }
        if (emptied) emptied.hidden = true;
      });
    }

    /**
     * Lifts the box out of the panel before the panel is replaced or removed,
     * which would otherwise take the box with it.
     */
    detachSearchBox() {
      if (!this.form || !this.formHome) return;
      if (this.form.parentNode === this.formHome) return;

      this.withFocusKept(() => this.formHome.append(this.form));
    }

    /**
     * Runs a move that reparents the box, putting the caret back afterwards.
     * Reparenting a focused input blurs it and drops the selection, which would
     * interrupt anyone still typing when a swap lands.
     * @param {Function} move - The reparenting to perform.
     */
    withFocusKept(move) {
      const active = document.activeElement === this.input;
      const start = active ? this.input.selectionStart : null;
      const end = active ? this.input.selectionEnd : null;

      move();

      if (!active) return;

      // preventScroll: the box is re-seated on every panel render, and a plain
      // focus() scrolled the page to it after the results had scrolled into view.
      this.input.focus({ preventScroll: true });
      if (start !== null) this.input.setSelectionRange(start, end);
    }

    /**
     * The device filter currently in the address bar, if any.
     * @returns {string}
     */
    activeDevice() {
      // A collection with its own facets applies this filter natively, with
      // accurate counts and the full value list. Taking it over would replace
      // both with search results that do not match what is on the page.
      if (this.dataset.noNativeFilters !== 'true') return '';
      return new URLSearchParams(window.location.search).get(DEVICE_FILTER) || '';
    }

    /**
     * Restores results when the page is loaded with a query or a picked device
     * in the URL (a reload, a shared link, or a link back into a search).
     */
    bootstrap() {
      const params = new URLSearchParams(window.location.search);
      const term = params.get('q') || '';

      // A shared or reloaded local-mode URL carries only the term; there is no
      // server render to replay, so re-run the filter once the index is up.
      if (this.mode === 'local') {
        if (!term) return;
        this.input.value = term;
        // A pagination link is an ordinary link, so arriving on page 3 of a
        // search is a fresh load that has to resume on page 3.
        this.localPageWanted = Number(params.get('page')) || 1;
        this.runLocal(false);
        return;
      }

      const device = this.activeDevice();

      // The stand-in term only means anything alongside a device; on its own it
      // would ask Shopify for a 9,000-product search that carries no facets.
      if (!device && !term) return;

      // A term equal to the device is this element's own stand-in, not something
      // the shopper typed, so it must not appear in the box.
      this.input.value = term === device ? '' : term;

      // A URL pushed by facet-filters.js already holds the full parameter set,
      // so replay it verbatim rather than rebuilding a bare search.
      const searchParams = params.has('type') ? params : this.buildParams(params.get('page'));
      this.fetchAndSwap(searchParams, false);
    }

    handleSubmit(evt) {
      evt.preventDefault();
      const term = this.input.value.trim();

      // Dismisses the on-screen keyboard, which otherwise covers the results
      // the shopper just asked for. Covers Enter and the submit button alike.
      this.input.blur();

      if (this.mode === 'local') {
        clearTimeout(this.localTimer);
        this.localPageWanted = 1;

        // Enter in the mobile filter drawer means "show me": close it onto the
        // results rather than leave them hidden behind it.
        const facets = document.querySelector('facet-filters');
        if (facets && this.form.closest('facet-filters') && facets.getAttribute('role') === 'dialog'
          && facets.getAttribute('aria-hidden') === 'false') {
          facets.close();
        }

        if (term) {
          this.runLocal();
        } else {
          this.restore();
        }
        return;
      }

      if (!term && !this.activeDevice()) {
        this.restore();
        return;
      }

      this.fetchAndSwap(this.buildParams());
    }

    /**
     * Handles history changes (e.g. back button clicked).
     *
     * States that carry a query are handled by facet-filters.js, which is
     * listening too - this only needs to catch the return to the unsearched
     * collection.
     */
    handleHistoryChange() {
      const params = new URLSearchParams(window.location.search);

      // Local mode: facet-filters.js replays the entry's filters, and its
      // on:facet-filters:updated lays the term back over them - so all this has
      // to do is put the entry's term in the box before that lands.
      if (this.mode === 'local' && params.get('q')) {
        this.localRun = (this.localRun || 0) + 1;
        this.input.value = params.get('q');
        this.localTerm = this.input.value.trim();
        return;
      }

      if (params.has('q') || this.activeDevice()) return;
      this.restore(false);
    }

    /**
     * Intercepts pagination links, which point at /search and would otherwise
     * navigate away from the collection.
     * @param {object} evt - Event object.
     */
    handleResultsClick(evt) {
      const link = evt.target.closest('.js-pagination a');
      if (!link) return;

      const facetFilters = document.querySelector('facet-filters');
      const form = document.getElementById('facets');
      if (!facetFilters || !form || typeof facetFilters.applyFilters !== 'function') return;

      // Only intercept while the grid is showing search results.
      const params = new URLSearchParams(new FormData(form));
      if (!params.get('q') && !params.get(DEVICE_FILTER)) return;

      evt.preventDefault();

      const page = new URL(link.href, window.location.origin).searchParams.get('page');
      if (page) {
        params.set('page', page);
      } else {
        params.delete('page');
      }

      facetFilters.applyFilters(params.toString(), evt);
    }

    /**
     * Builds the search parameters from the form, which holds the query term,
     * the result type and the collection's scope filter.
     * @param {string} [page] - Page number to request.
     * @returns {URLSearchParams}
     */
    buildParams(page) {
      const params = new URLSearchParams(new FormData(this.form));
      const term = this.input.value.trim();
      const device = this.activeDevice();

      // The form holds the scope and the term; everything else the shopper has
      // chosen - the device, a sort, a price range - lives in the URL and would
      // be dropped by rebuilding from the form alone.
      new URLSearchParams(window.location.search).forEach((value, name) => {
        if (name === 'q' || name === 'page' || !value) return;
        if (!params.has(name)) params.set(name, value);
      });

      if (device) params.set(DEVICE_FILTER, device);
      params.set('q', term || device);

      if (page) params.set('page', page);
      return params;
    }

    /**
     * Forces the collection's scope filter onto a set of search parameters.
     *
     * Called by facet-filters.js for every request it makes while a search is
     * active, so that no interaction - "clear all" in particular - can widen
     * the results past the collection the shopper is browsing.
     * @param {string} searchParams - Filter/sort search parameters.
     * @returns {string}
     */
    withScope(searchParams) {
      const params = new URLSearchParams(searchParams);

      this.form.querySelectorAll('input[type="hidden"][name^="filter."]').forEach((input) => {
        params.set(input.name, input.value);
      });

      // The device is re-asserted the same way, and for the same reason. Relying
      // on its checkbox in the panel was not enough: Shopify only renders that
      // value when it happens to fall inside the facet it returns, so clearing
      // one filter - or ticking a colour while a word was typed - dropped the
      // shopper's phone and widened the results to the whole collection.
      // "Clear all" is handled separately, by leaving the search entirely.
      const device = this.activeDevice();
      if (device) params.set(DEVICE_FILTER, device);

      return params.toString();
    }

    /**
     * Reduces a request's parameters to the ones worth showing a shopper.
     *
     * The endpoint needs the result type, the collection's scope and a term;
     * none of that is the shopper's choice, and the stand-in term in particular
     * must never be seen or shared. What is left is what they picked.
     * @param {string} searchParams - Filter/sort search parameters.
     * @returns {string}
     */
    visibleParams(searchParams) {
      const params = new URLSearchParams(searchParams);
      const hidden = new Set();
      this.form.querySelectorAll('input[type="hidden"]').forEach((input) => hidden.add(input.name));

      const term = (params.get('q') || '').trim();
      const visible = new URLSearchParams();
      const device = params.get(DEVICE_FILTER) || '';
      if (term && term !== device) visible.set('q', term);

      params.forEach((value, name) => {
        if (name === 'q' || hidden.has(name) || !value) return;
        visible.set(name, value);
      });

      return visible.toString();
    }

    /**
     * Fetches scoped search results and swaps them into the collection grid.
     * @param {URLSearchParams} searchParams - Search parameters.
     * @param {boolean} [updateUrl=true] - Update url with the search term.
     */
    async fetchAndSwap(searchParams, updateUrl = true) {
      const fetchUrl = `${this.dataset.searchUrl}?${searchParams}&section_id=${this.dataset.sectionId}`;

      this.results.classList.add('is-loading');

      try {
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        // Before cachePristine, so the cached panel never contains the box -
        // restoring it later would otherwise put a second one on the page.
        this.detachSearchBox();

        this.cachePristine();

        // Filters are absent from an oversized collection, so the first search
        // injects the whole panel rather than updating one.
        const newFilters = tmpl.content.querySelector('.main-products-grid__filters');
        const currentFilters = this.grid.querySelector('.main-products-grid__filters');

        if (newFilters && currentFilters) {
          currentFilters.replaceWith(newFilters);
        } else if (newFilters) {
          this.grid.insertAdjacentElement('afterbegin', newFilters);
        } else if (currentFilters) {
          currentFilters.remove();
        }

        const newResults = tmpl.content.getElementById('filter-results');
        this.results.innerHTML = newResults ? newResults.innerHTML : '';

        // The "no results" message sits outside #filter-results in the search
        // section, so it has to be moved across explicitly.
        const noResults = tmpl.content.getElementById('search-no-results');
        if (noResults) this.results.prepend(noResults);

        this.dropDeviceFacet();
        this.stripFilterCounts();
        this.applyGridLayout();
        this.mountSearchBox();

        // Must come after the filters are in place: products-toolbar.js looks
        // for them when it upgrades, and only shows its filter toggle if found.
        const newToolbar = tmpl.content.getElementById('products-toolbar');
        if (newToolbar && this.toolbar) {
          this.toolbar.replaceWith(newToolbar);
          this.toolbar = newToolbar;
          this.rebindSort();
        }

        if (updateUrl) {
          const query = this.visibleParams(searchParams);

          // The full parameter set goes into the history state so that
          // facet-filters.js can replay it on back/forward.
          window.history.pushState(
            { searchParams: searchParams.toString() },
            '',
            query ? `${window.location.pathname}?${query}` : window.location.pathname
          );
        }

        document.dispatchEvent(new CustomEvent('on:facet-filters:updated', { bubbles: true }));
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        clearTimeout(this.maskTimer);
        this.results.classList.remove('is-loading');
        // Whatever the outcome, the page must not stay hidden behind the
        // entry marker.
        document.documentElement.classList.remove('collection-search-loading');
        // Whatever happened - an abandoned run, a failed fetch - the grid must
        // never be left masked.
        this.grid.classList.remove('is-search-pending');
      }
    }

    /**
     * Removes the counts from the swapped-in facets.
     *
     * Shopify counts a facet value over the text-matched set rather than the one
     * it returns products for, and the two diverge as the set grows: on mobilskal
     * the colour facet offered "Svart (30)" where applying it returns 62 products,
     * every one of them correct. The number cannot be made right on this path, so
     * it is removed rather than shown wrong.
     *
     * Collections that filter natively keep their counts, which were measured
     * exact - "iPhone 17 (14)" against 14 results, "(38)" against 38, "(23)"
     * against 23.
     */
    stripFilterCounts() {
      const panel = this.grid.querySelector('.main-products-grid__filters');
      if (!panel) return;

      panel.querySelectorAll('.filter__count').forEach((count) => count.remove());
    }

    /**
     * Removes the device facet from the swapped-in panel.
     *
     * Shopify builds a facet's values from everything the *term* matched, before
     * that facet is applied - so searching "Xiaomi 15 Ultra" offers iPhone 15 Pro
     * and OnePlus 15 alongside it, none of which are in the results. Every other
     * facet is computed after the device filter and does describe what is on the
     * page, so only this one goes.
     *
     * No loss: on these collections the picker above the grid is the device
     * switcher, and it holds every model rather than the hundred Shopify caps a
     * facet at.
     *
     * Hidden rather than removed. The checked box in this group is what carries
     * the device through the facets form, so removing it made the next click on
     * any other filter silently drop the device and widen the results.
     */
    dropDeviceFacet() {
      const panel = this.grid.querySelector('.main-products-grid__filters');
      if (!panel) return;

      // Each facet is its own <details class="filter">; take only that one.
      panel.querySelectorAll(`input[name="${DEVICE_FILTER}"]`).forEach((input) => {
        const group = input.closest('details.filter');
        if (group) group.hidden = true;
      });
    }

    /**
     * Gives the swapped-in results the collection grid's own column layout.
     *
     * The search section is fetched on its own, so it renders with its schema
     * defaults rather than the settings this collection was configured with -
     * three cards across where the collection shows five. Copying the list's
     * attributes rather than hardcoding classes means it follows whatever the
     * merchant later picks in the theme editor.
     */
    applyGridLayout() {
      if (!this.gridAttrs) return;

      const list = this.results.querySelector('ul.grid');
      if (!list) return;

      // An oversized collection renders with no filter panel, so its grid carries
      // the wider "filters closed" columns (5 at xl). When a search or the device
      // filter brings the sidebar in, switch to the section's own "filters open"
      // columns (4), or five cards squeeze in beside the 240px sidebar.
      const attrs = { ...this.gridAttrs };
      if (this.grid.querySelector('.main-products-grid__filters') && attrs['data-filters-open-classes']) {
        attrs.class = attrs['data-filters-open-classes'];
      }

      Object.entries(attrs).forEach(([name, value]) => {
        if (value === null) {
          list.removeAttribute(name);
        } else {
          list.setAttribute(name, value);
        }
      });
    }

    /**
     * Re-points facet-filters.js at the replacement sort control.
     *
     * It caches `.products-toolbar__sort` when it upgrades, which happens as
     * the filters are injected - a moment before the toolbar is swapped - so
     * the reference it holds is detached. The control is brand new here, so
     * there's no listener to duplicate.
     */
    rebindSort() {
      const facetFilters = document.querySelector('facet-filters');
      if (!facetFilters || !facetFilters.sortingEnabled) return;

      const sortBy = this.toolbar.querySelector('.products-toolbar__sort');
      if (!sortBy) return;

      facetFilters.desktopSortBy = sortBy;
      sortBy.addEventListener('change', facetFilters.handleFilterChange.bind(facetFilters));
    }

    /**
     * Stores the unsearched grid so clearing the search doesn't need a refetch.
     */
    cachePristine() {
      if (this.pristine) return;

      // A collection that filters natively has a panel here already, and it is
      // the shopper's unsearched one - counts and all. Keeping it means clearing
      // the search can put it back rather than leaving the page with no filters.
      // Null on an oversized collection, which genuinely has none to keep.
      const panel = this.grid.querySelector('.main-products-grid__filters');

      // What the url said when this was cached, so a restore can put the two
      // back together, and the filters it was cached under, so local mode can
      // tell when it no longer describes the page.
      this.pristineKey = this.filterKey();
      const pristineParams = new URLSearchParams(window.location.search);
      pristineParams.delete('q');
      pristineParams.delete('page');
      this.pristineUrl = pristineParams.toString()
        ? `${window.location.pathname}?${pristineParams}`
        : window.location.pathname;

      this.pristine = {
        results: this.results.innerHTML,
        toolbar: this.toolbar ? this.toolbar.outerHTML : null,
        filters: panel ? panel.outerHTML : null
      };

      // The unsearched total, so clearing a local search can put the real
      // collection count back rather than the number of cards on this page.
      const count = document.querySelector('.products-toolbar__count');
      if (count) {
        const digits = count.textContent.replace(/[\s .,]/g, '').match(/^\d+/);
        this.pristineTotal = digits ? Number(digits[0]) : null;
      }

      const list = this.results.querySelector('ul.grid');
      this.gridAttrs = list
        ? {
            class: list.getAttribute('class'),
            'data-filters-open-classes': list.getAttribute('data-filters-open-classes'),
            'data-filters-closed-classes': list.getAttribute('data-filters-closed-classes')
          }
        : null;
    }

    /**
     * Returns the collection to its unsearched state.
     * @param {boolean} [updateUrl=true] - Remove the search term from the url.
     */
    restore(updateUrl = true) {
      this.localRun = (this.localRun || 0) + 1;
      this.input.value = '';
      this.localTerm = '';
      this.resetFacets();
      this.grid.classList.remove('is-local-search');
      this.grid.classList.remove('is-search-pending');
      document.documentElement.classList.remove('collection-search-loading');
      this.localPageWanted = 1;

      // The pristine copy is the unfiltered collection as it was on load, so it
      // is the wrong thing to restore once facets are applied - it would drop
      // the shopper's filters along with their search. Hand back to
      // facet-filters.js instead and let it re-render the current facets.
      // Also when the filters changed since the copy was taken: it would show
      // results for filters that are no longer applied.
      if (this.mode === 'local' && (this.hasActiveFilters() || (this.pristine && this.pristineKey !== this.filterKey()))) {
        const facets = document.querySelector('facet-filters');
        if (facets && facets.applyFilters) {
          if (updateUrl) {
            window.history.replaceState(
              { searchParams: this.activeFilters().toString() },
              '',
              this.localUrl('', 1)
            );
          }
          facets.applyFilters(this.activeFilters().toString(), null, false);
          this.mountSearchBox();
          return;
        }
      }

      // Scoped mode with nothing cached has no search on screen to undo - an
      // empty submit, or a clear, before any search ran. Pushing the bare url
      // there dropped the filters from the address bar while they stayed ticked.
      if (this.mode !== 'local' && !this.pristine) {
        this.mountSearchBox();
        return;
      }

      if (this.pristine) {
        // Local mode only ever replaced the results, so the panel on screen is
        // still the live one - putting the cached copy back would throw away
        // whatever the shopper has ticked since.
        if (this.mode !== 'local') {
          // Same hazard as the swap: the panel about to go may be holding the box.
          this.detachSearchBox();
          this.restoreFilters();
        }

        this.results.innerHTML = this.pristine.results;

        if (this.mode === 'local' && this.countTemplate != null) {
          const count = document.querySelector('.products-toolbar__count');
          const grid = this.results.querySelectorAll('ul.grid > li').length;
          if (count) this.setLocalCount(this.pristineTotal ?? grid);
        }

        if (this.toolbar && this.pristine.toolbar) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = this.pristine.toolbar;
          const restored = tmpl.content.getElementById('products-toolbar');

          if (restored) {
            this.toolbar.replaceWith(restored);
            this.toolbar = restored;
            // facet-filters.js still listens on the sort control just replaced.
            this.rebindSort();
          }
        }
      }

      // A null state keeps facet-filters.js from trying to replay this entry.
      // Scoped mode put the cached panel back, so the url goes back to the one
      // cached with it - otherwise its ticks sat under a url without them.
      if (updateUrl) {
        const url = this.mode !== 'local' && this.pristine && this.pristineUrl ? this.pristineUrl : window.location.pathname;
        window.history.pushState(null, '', url);
      }

      // Scoped mode is back on the page it cached, so the copy is spent. Kept,
      // a later Back through the collection's own filter history put it back
      // again, over the render facet-filters.js was replaying for that entry.
      // The next search caches afresh.
      if (this.mode !== 'local') this.pristine = null;

      this.mountSearchBox();
    }

    /**
     * Whether the shopper has any facet applied right now.
     * @returns {boolean} True when the url carries a filter parameter.
     */
    hasActiveFilters() {
      return this.activeFilters().toString() !== '';
    }

    /**
     * The facets and sort currently applied, as request parameters.
     *
     * These ride along on every collection fetch local mode makes, which is what
     * lets a search and the filters narrow the same set instead of replacing one
     * another. products.json cannot help here - measured: it returns the same
     * 250 products with or without a filter parameter, so it has no idea facets
     * exist. The section render does honour them.
     * @returns {URLSearchParams} The active filter and sort parameters.
     */
    activeFilters() {
      const active = new URLSearchParams();

      new URLSearchParams(window.location.search).forEach((value, key) => {
        if (!value) return;
        if (key.startsWith('filter.') || key === 'sort_by') active.append(key, value);
      });

      return active;
    }

    /**
     * A url carrying the term, the active facets and a page together, so that
     * following a result page keeps the shopper's filters and reloading keeps
     * their search. One builder for both, or the two drift apart.
     * @param {string} term - The active term.
     * @param {number} page - 1-based results page.
     * @returns {string} The url to show or link to.
     */
    localUrl(term, page) {
      const params = this.activeFilters();
      if (term) params.set('q', term);
      if (page > 1) params.set('page', String(page));

      const query = params.toString();
      return query ? `${window.location.pathname}?${query}` : window.location.pathname;
    }

    /**
     * Identifies the current filter state, so a cached walk is only reused while
     * the facets it was built under still apply.
     * @returns {string} A key for the active filter state.
     */
    filterKey() {
      const active = [...this.activeFilters().entries()].map(([k, v]) => `${k}=${v}`);
      return active.sort().join('&');
    }

    /**
     * The collection's untranslated handle. products.json only answers to it -
     * measured 2026-10-01: skalhuset.dk/collections/apple-watch-rem-1/products.json
     * is an empty list, while /collections/apple-watch-armband/products.json on
     * the same domain returns the products, in Danish. Liquid only exposes the
     * translated handle, but Shopify's own hreflang tag for the Swedish store
     * carries the original.
     * @returns {string} The handle products.json understands.
     */
    baseHandle() {
      const link = document.querySelector('link[rel="alternate"][hreflang="sv"]');
      const match = link && link.href.match(/\/collections\/([^/?#]+)/);
      return match ? match[1] : this.dataset.collectionHandle;
    }

    /**
     * The collection's own products, fetched once and kept for the session.
     *
     * products.json is the only public endpoint that returns collection
     * membership exactly - measured at 613/613 and 290/290 against the counts
     * the collection pages render. Everything else about scoping a search to a
     * collection is an approximation; this is not.
     * @returns {Promise<Array>} The indexed rows.
     */
    localIndex() {
      if (this.indexPromise) return this.indexPromise;

      this.indexPromise = (async () => {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const base = `${root}collections/${this.baseHandle()}/products.json`;

        // The section already knows how big the collection is, so every page can
        // go out at once instead of one round trip after another - waiting for
        // each in turn is what made the first search take seconds.
        const total = Number(this.dataset.collectionCount || 0);
        const pages = Math.min(
          Math.max(Math.ceil(total / LOCAL_PAGE_SIZE), 1),
          LOCAL_MAX_PAGES
        );

        const batches = await Promise.all(
          Array.from({ length: pages }, (unused, i) => fetch(`${base}?limit=${LOCAL_PAGE_SIZE}&page=${i + 1}`)
            .then((res) => (res.ok ? res.json() : null))
            .then((body) => (body && body.products) || [])
            .catch(() => []))
        );

        const rows = [];
        // Kept in page order, because the section render agrees on it - that is
        // what lets a row's position say which collection page holds its card.
        batches.forEach((batch) => {
          batch.forEach((product) => {
            const row = this.toRow(product);
            row.pos = rows.length;
            rows.push(row);
          });
        });

        return rows;
      })();

      return this.indexPromise;
    }

    /**
     * Every product's facet values, fetched once and kept for the session.
     *
     * Shopify builds the filter list from the whole collection, so a search
     * that leaves seven products still offers the other three hundred's
     * colours and brands - and picking one lands on "0 produkter". Narrowing
     * the list needs each matching product's facet values, and nothing already
     * on hand carries them: products.json has no metafields, and most of the
     * filters here are metafields. The alternative is one section render per
     * rendered value, which on this collection is sixty-one renders a search.
     *
     * templates/collection.facetdata.liquid emits them instead, as a few
     * kilobytes a page. Verified against the panel Shopify renders for ljud:
     * the same 76 values across all six groups, none missing, none invented.
     *
     * Fetched without the active facets - Shopify applies those to this view as
     * well, and a narrowed feed would narrow the list against a subset.
     * @returns {Promise<object|null>} Keys and values by handle, or null.
     */
    facetData() {
      if (this.facetPromise) return this.facetPromise;

      this.facetPromise = (async () => {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const base = `${root}collections/${this.dataset.collectionHandle}?view=facetdata`;
        const get = (page) => fetch(page > 1 ? `${base}&page=${page}` : base)
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null);

        const first = await get(1);
        if (!first || !first.keys || !first.keys.length) return null;

        const pages = Math.min(first.pages || 1, FACET_MAX_PAGES);
        const rest = await Promise.all(
          Array.from({ length: Math.max(pages - 1, 0) }, (unused, i) => get(i + 2))
        );

        // A page that failed would leave its products holding no values, and a
        // value hidden for want of data is a product the shopper cannot reach.
        // Better no narrowing than wrong narrowing.
        if (rest.some((body) => !body || !body.rows)) return null;

        const values = new Map();
        [first].concat(rest).forEach((body) => {
          body.rows.forEach((row) => values.set(row.h, row.v));
        });

        return { keys: first.keys, values };
      })().catch(() => null);

      return this.facetPromise;
    }

    /**
     * Whether one product carries one value in one filter group.
     * @param {object} data - The feed.
     * @param {string} handle - The product.
     * @param {string} key - The filter's parameter name.
     * @param {string} value - The value to test.
     * @returns {boolean} True when the product has it.
     */
    rowHasValue(data, handle, key, value) {
      const at = data.keys.indexOf(key);
      if (at === -1) return false;

      const held = (data.values.get(handle) || [])[at];
      if (held === null || held === undefined) return false;

      return Array.isArray(held) ? held.indexOf(value) !== -1 : String(held) === value;
    }

    /**
     * Narrows the filter list to the values the search actually leaves.
     *
     * A group's own values are counted with that group's selection lifted and
     * every other one applied, which is how Shopify counts them too: ticking
     * "Blå" must not reduce the colour list to Blå.
     *
     * Values are hidden rather than removed, and a ticked one is always kept -
     * it is what the shopper unticks to get back.
     * @param {string} term - The active term.
     */
    async narrowFacets(term, run) {
      const panel = this.grid.querySelector('.main-products-grid__filters');
      if (!panel || this.mode !== 'local' || !term) return;

      const data = await this.facetData();
      if (!data) return;

      const groups = new Map();
      const price = {};
      let evaluable = true;

      this.activeFilters().forEach((value, key) => {
        if (key === 'sort_by') return;
        if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') {
          price[key.slice(-3)] = Number(value);
          return;
        }
        if (data.keys.indexOf(key) === -1) {
          evaluable = false;
          return;
        }
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(value);
      });

      // An applied filter this feed cannot evaluate would leave every count too
      // high, so the list is left exactly as Shopify rendered it.
      if (!evaluable) return;

      const matches = this.localMatches(term, await this.localIndex());
      if (this.input.value.trim() !== term || (run && run !== this.localRun)) return;

      // Start from the list as the collection rendered it. Narrowing only ever
      // hides, so without this a new term inherits the last one's hiding -
      // "grön" then "blå" left no colours at all.
      this.resetFacets();

      const passes = (row, skip) => {
        if (price.gte !== undefined || price.lte !== undefined) {
          if (row.price === null) return false;
          if (price.gte !== undefined && row.price < price.gte) return false;
          if (price.lte !== undefined && row.price > price.lte) return false;
        }

        return [...groups].every(([key, values]) => key === skip
          || values.some((value) => this.rowHasValue(data, row.handle, key, value)));
      };

      data.keys.forEach((key) => {
        const inputs = [...panel.querySelectorAll(`input[name="${key}"]`)];
        if (!inputs.length) return;

        const options = inputs.map((input) => input.closest('li')).filter(Boolean);
        const candidates = matches.filter((row) => passes(row, key));

        // The theme caps each group at max_filter_options and marks the rest
        // js-hidden, counting from the top of the full list. Read the cap off
        // that before touching anything - a group with nothing marked was not
        // capped, or the shopper has already pressed "show more".
        const capped = options.some((option) => option.classList.contains('js-hidden'));
        const cap = capped
          ? options.filter((option) => !option.classList.contains('js-hidden')).length
          : options.length;

        let left = 0;

        options.forEach((option) => {
          const input = option.querySelector('input');
          const count = candidates.reduce(
            (n, row) => n + (this.rowHasValue(data, row.handle, key, input.value) ? 1 : 0),
            0
          );

          const badge = option.querySelector('.filter__count');
          if (badge) badge.textContent = `(${count})`;

          if (option.dataset.searchCapped === undefined) {
            option.dataset.searchCapped = option.classList.contains('js-hidden') ? '1' : '0';
          }

          if (count === 0 && !input.checked) {
            option.hidden = true;
            option.dataset.searchHidden = '1';
            return;
          }

          // The cap has to fall on the values that survived, not on the ones
          // the server ranked first. Otherwise a group whose three remaining
          // brands all sat past the cap shows one of them, or none - which is
          // what "10 items but no filters" looked like.
          left += 1;
          option.classList.toggle('js-hidden', left > cap);
        });

        const group = inputs[0].closest('details.filter');

        // "Show more" with nothing behind it is a dead control.
        const more = group && group.querySelector('.js-show-more');
        if (more) more.hidden = left <= cap;

        // A group with nothing left in it is noise; the device facet is hidden
        // the same way, so the flag says which of the two did it.
        if (group && !left) {
          group.hidden = true;
          group.dataset.searchHidden = '1';
        }
      });

      // Counts are hidden until this point, because until now they described
      // the unsearched collection.
      this.grid.classList.add('is-facets-narrowed');
    }

    /**
     * Puts back whatever narrowFacets hid, and only that - the device facet is
     * hidden by the same property and must stay hidden.
     */
    resetFacets() {
      const panel = this.grid.querySelector('.main-products-grid__filters');
      if (!panel) return;

      this.grid.classList.remove('is-facets-narrowed');

      panel.querySelectorAll('[data-search-hidden]').forEach((node) => {
        node.hidden = false;
        delete node.dataset.searchHidden;
      });

      // The theme removes "show more" once it is pressed, so a group without
      // one was expanded by the shopper and stays expanded - collapsing it again
      // would leave no way to open it.
      panel.querySelectorAll('[data-search-capped]').forEach((option) => {
        const group = option.closest('details.filter');
        const expanded = group && !group.querySelector('.js-show-more');
        option.classList.toggle('js-hidden', option.dataset.searchCapped === '1' && !expanded);
        delete option.dataset.searchCapped;
      });

      panel.querySelectorAll('.js-show-more').forEach((more) => {
        more.hidden = false;
      });
    }

    /**
     * The handles the active facets leave, in the collection's own order.
     *
     * Walks the collection's section render with the facets applied - the only
     * endpoint that honours them - and files every card it sees on the way, so
     * the cards for the results are already in hand when it finishes. The walk
     * is bounded by the count the first page reports, and cached per filter
     * state, so changing a facet re-walks and nothing else does.
     * @returns {Promise<Array>} Handles passing the active facets, in order.
     */
    filteredHandles() {
      const key = this.filterKey();
      if (this.filteredKey === key && this.filteredPromise) return this.filteredPromise;

      this.filteredKey = key;
      this.filteredPromise = (async () => {
        // Page one first, because only the render knows how many the facets
        // leave; the rest then go out together rather than one at a time.
        const first = await this.cacheCollectionPage(1, { collect: true });
        if (!first) return [];

        const perPage = first.handles.length || LOCAL_GRID_PAGE;
        const lastPage = first.total
          ? Math.min(Math.ceil(first.total / perPage), LOCAL_MAX_PAGES * 2)
          : 1;

        const rest = await Promise.all(
          Array.from({ length: Math.max(lastPage - 1, 0) },
            (unused, i) => this.cacheCollectionPage(i + 2, { collect: true }))
        );

        return rest.reduce(
          (all, page) => (page ? all.concat(page.handles) : all),
          first.handles.slice()
        );
      })();

      return this.filteredPromise;
    }

    /**
     * Flattens a product into the fields the results need plus one lowercase
     * haystack, so matching does no work per keystroke beyond a substring test.
     * @param {object} product - A product from products.json.
     * @returns {object} The indexed row.
     */
    toRow(product) {
      const variants = product.variants || [];
      const cheapest = variants.reduce(
        (low, v) => (low === null || Number(v.price) < Number(low.price) ? v : low),
        null
      ) || {};

      return {
        handle: product.handle,
        title: product.title,
        image: (product.images && product.images[0] && product.images[0].src) || '',
        price: cheapest.price != null ? Number(cheapest.price) : null,
        compareAt: cheapest.compare_at_price != null ? Number(cheapest.compare_at_price) : null,
        haystack: [product.title, product.vendor, product.product_type, (product.tags || []).join(' ')]
          .join(' ')
          .toLowerCase()
      };
    }

    /**
     * Every row matching all of the term's words, in collection order.
     * @param {string} term - What the shopper typed.
     * @param {Array} rows - The index.
     * @returns {Array} Matching rows.
     */
    localMatches(term, rows) {
      const words = term.toLowerCase().split(/\s+/).filter(Boolean);
      if (!words.length) return [];
      return rows.filter((row) => words.every((word) => row.haystack.includes(word)));
    }

    /**
     * Filters the collection against the box and renders the result.
     * @param {boolean} [updateUrl=true] - Put the term in the address bar.
     */
    async runLocal(updateUrl = true) {
      const term = this.input.value.trim();
      if (!term) {
        this.restore(updateUrl);
        return;
      }

      // Runs overlap: a facet change re-runs the search while a keystroke's run
      // is still waiting on the index or the facets. The term alone cannot tell
      // them apart - both can be "svart" - so an older run landing last painted
      // results for filters that no longer applied.
      this.localRun = (this.localRun || 0) + 1;
      const run = this.localRun;

      this.results.classList.add('is-loading');
      this.grid.classList.remove('is-facets-narrowed');

      // Started, not awaited: the filter list is narrowed after the results are
      // up, so the feed may as well load alongside them rather than after.
      this.facetData();

      try {
        const rows = await this.localIndex();

        // The words are matched against the whole collection's index, then the
        // result is narrowed to what the facets leave. Searching and filtering
        // therefore compose instead of replacing one another: the index answers
        // "does this product match the words", the facets answer "is it still on
        // the page", and a product has to satisfy both.
        let matches = this.localMatches(term, rows);

        // The walk comes back in the render's order, which honours sort_by, so the
        // matches take its order too rather than products.json's default one.
        if (this.hasActiveFilters()) {
          const byHandle = new Map(matches.map((row) => [row.handle, row]));
          matches = (await this.filteredHandles()).map((handle) => byHandle.get(handle)).filter(Boolean);
        }

        // The shopper may have typed on, or changed a filter, while this waited.
        if (run !== this.localRun || this.input.value.trim() !== term) return;

        await this.renderLocal(term, matches, run);

        // Not awaited: the results are already on screen, and the filter list
        // catching up a moment later beats holding them back for it. Called
        // here rather than inside renderLocal so that a search with no results
        // is narrowed too - that is the dead end, and the list is the way out.
        if (run !== this.localRun) return;
        this.narrowFacets(term, run);

        if (updateUrl) {
          // replaceState, not push: typing would otherwise leave one history
          // entry per keystroke for Back to crawl through. The filters ride in
          // the state as well, because facet-filters.js replays an entry from
          // its searchParams - without them Back rendered the bare collection
          // under a url that still named the search.
          window.history.replaceState(
            { localTerm: term, searchParams: this.activeFilters().toString() },
            '',
            this.localUrl(term, 1)
          );
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Shows the matching products, using the theme's own product cards.
     *
     * The cards are fetched, not built. Each page of the collection's own
     * section render is pulled and the matching cards are kept, which is the
     * only way to get a real card - border, price, quick add, swatches,
     * compare, image carousel. A hand-built card gets all of that wrong, and no
     * server render returns exactly this set of products.
     *
     * Results page the way the collection does - numbered pages of the same
     * size, driven by real links - rather than scrolling endlessly, so a search
     * behaves like the page it replaced.
     * @param {string} term - What the shopper typed.
     * @param {Array} rows - Matching rows, in collection order.
     */
    async renderLocal(term, rows, run) {
      this.cachePristine();

      this.localTerm = term;
      this.setLocalCount(rows.length);
      this.grid.classList.add('is-local-search');

      if (!rows.length) {
        this.results.innerHTML = '';
        const empty = document.createElement('p');
        empty.className = 'collection-search-local__empty rte';
        empty.setAttribute('role', 'status');
        empty.textContent = (this.dataset.noResultsText || '').replace('[]', term);
        this.results.appendChild(empty);
        this.grid.classList.remove('is-search-pending');
        document.documentElement.classList.remove('collection-search-loading');
        return;
      }

      const perPage = this.localPerPage || LOCAL_GRID_PAGE;
      const pages = Math.ceil(rows.length / perPage);
      const page = Math.min(Math.max(this.localPageWanted || 1, 1), pages);
      const slice = rows.slice((page - 1) * perPage, page * perPage);

      const cards = await this.cardsFor(slice, perPage);
      if (this.localTerm !== term || (run && run !== this.localRun)) return;

      // Rebuild from the pristine markup so the grid keeps the classes the
      // merchant configured, then swap in this page's cards and pagination.
      // gridAttrs is null when the pristine markup had no grid to rebuild in.
      this.results.innerHTML = this.gridAttrs || !this.resultsShell ? this.pristine.results : this.resultsShell;
      const grid = this.results.querySelector('ul.grid');
      if (grid) {
        grid.innerHTML = '';
        cards.forEach((card) => grid.appendChild(card));
      }

      this.results.querySelectorAll('.js-pagination').forEach((node) => {
        const replacement = this.localPagination(term, page, pages);
        if (replacement) {
          node.replaceWith(replacement);
        } else {
          node.remove();
        }
      });

      this.grid.classList.remove('is-search-pending');
      document.documentElement.classList.remove('collection-search-loading');

      if (this.scrollOnNextRender) {
        this.scrollOnNextRender = false;
        this.scrollToResults();
      }
    }

    /**
     * The rendered card for each row, fetched from the collection a page at a
     * time and kept, so paging back and forth costs nothing.
     * @param {Array} rows - The rows to place, in order.
     * @param {number} perPage - The collection's own page size.
     * @returns {Promise<Array>} Card elements, in the same order.
     */
    async cardsFor(rows, perPage) {
      this.cardCache = this.cardCache || new Map();

      // products.json and the section render agree on order, so a row's
      // position says which collection page holds it - usually one or two
      // fetches rather than a walk from the start.
      const wanted = rows.filter((row) => !this.cardCache.has(row.handle));
      const pages = [...new Set(wanted.map((row) => Math.floor(row.pos / perPage) + 1))];

      // products.json and the section render agree on order, so a row's
      // position says which collection page holds it - usually one or two
      // fetches, and they go out together.
      await Promise.all(pages.map((page) => this.cacheCollectionPage(page)));

      // Should the two orders ever disagree, fall back to walking until the
      // stragglers turn up rather than rendering a hole.
      const missing = () => rows.filter((row) => !this.cardCache.has(row.handle));
      const total = Number(this.dataset.collectionCount || 0);
      const lastPage = total ? Math.ceil(total / perPage) : LOCAL_MAX_PAGES;

      for (let page = 1; page <= lastPage && missing().length; page += 1) {
        if (this.fetchedPages && this.fetchedPages.has(page)) continue;
        // eslint-disable-next-line no-await-in-loop
        await this.cacheCollectionPage(page);
      }

      return rows.map((row) => this.cardCache.get(row.handle)).filter(Boolean);
    }

    /**
     * Fetches one page of the collection and files every card on it by handle.
     *
     * The active facets go on the request, so what comes back is the filtered
     * set when the shopper has filtered and the whole collection when they have
     * not. Pages are cached per filter state - changing a facet invalidates the
     * walk, not the cards, which stay valid however they were reached.
     * @param {number} page - 1-based page number.
     * @param {object} [opts] - Pass collect:true to get the page's contents back.
     * @returns {Promise<object|undefined>} Handles and total when collecting.
     */
    async cacheCollectionPage(page, opts = {}) {
      const key = this.filterKey();
      if (this.pagesKey !== key) {
        this.pagesKey = key;
        this.fetchedPages = new Set();
        this.pageHandles = new Map();
        this.inFlight = new Map();
      }
      this.fetchedPages = this.fetchedPages || new Set();
      this.pageHandles = this.pageHandles || new Map();
      this.inFlight = this.inFlight || new Map();
      this.cardCache = this.cardCache || new Map();

      const done = () => (opts.collect
        ? { handles: this.pageHandles.get(page) || [], total: this.filteredTotal }
        : undefined);

      if (this.fetchedPages.has(page)) return done();

      // Pages are requested in parallel now, so two callers can want the same
      // one at once. Sharing the in-flight request keeps that to one fetch.
      if (this.inFlight.has(page)) {
        await this.inFlight.get(page);
        return done();
      }

      const sectionId = this.collectionSectionId();
      if (!sectionId) return undefined;

      const params = this.activeFilters();
      params.set('section_id', sectionId);
      params.set('page', String(page));

      const request = (async () => {
        const res = await fetch(`${window.location.pathname}?${params}`);
        if (!res.ok) return;

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await res.text();

        // A grid to build results in, for when the page was cached showing "no
        // products" (a filter set with no hits) and so has none of its own.
        if (!this.resultsShell) {
          const shell = tmpl.content.getElementById('filter-results');
          if (shell && shell.querySelector('ul.grid')) this.resultsShell = shell.innerHTML;
        }

        const handles = [];
        const items = [...tmpl.content.querySelectorAll('li.js-pagination-result')];
        // Promotion blocks carry the same class but no product link; skipping
        // them here is what keeps them out of the results.
        items.forEach((item) => {
          const link = item.querySelector('a[href*="/products/"]');
          if (!link) return;
          const handle = link.getAttribute('href').split('/products/')[1].split('?')[0];
          if (!handle) return;
          handles.push(handle);
          if (!this.cardCache.has(handle)) this.cardCache.set(handle, item);
        });

        this.pageHandles.set(page, handles);
        this.fetchedPages.add(page);

        // The filtered total is only reported by the render itself; it is what
        // bounds the walk in filteredHandles().
        const counter = tmpl.content.querySelector('[data-products-count]');
        let total = null;
        if (counter) {
          const digits = counter.textContent.replace(/[\s\u00a0.,]/g, '').match(/^\d+/);
          if (digits) this.filteredTotal = total = Number(digits[0]);
        }

        // Only a page one with more products after it is certainly full, so only
        // it tells the grid's page size. A partial last page, or a filtered set
        // that fits on one page, would shrink every later search's pages.
        if (page === 1 && total && total > handles.length) this.localPerPage = handles.length;
      })();

      this.inFlight.set(page, request);
      try {
        await request;
      } finally {
        this.inFlight.delete(page);
      }

      return done();
    }

    /**
     * The id of the section that renders the grid. The facets form publishes it;
     * a collection with no filters has no facets form, so fall back to the
     * section wrapper around the grid.
     * @returns {string} The section id, or an empty string.
     */
    collectionSectionId() {
      if (this.sectionId != null) return this.sectionId;

      const facets = document.getElementById('facets');
      let id = (facets && facets.dataset.filterSectionId) || '';

      if (!id) {
        const host = this.grid.closest('[id^="shopify-section-"]');
        if (host) id = host.id.replace('shopify-section-', '');
      }

      this.sectionId = id;
      return id;
    }

    /**
     * Numbered pagination over the results, in the theme's own markup so it is
     * indistinguishable from the collection's. The links are real: following
     * one reloads with the term in the url, which bootstrap picks back up.
     * @param {string} term - The active term.
     * @param {number} page - The page being shown.
     * @param {number} pages - How many pages of results there are.
     * @returns {HTMLElement|null} The pagination list, or null when there is one page.
     */
    localPagination(term, page, pages) {
      if (pages < 2) return null;

      const list = document.createElement('ul');
      list.className = 'pagination pagination--page-buttons relative flex flex-wrap '
        + 'justify-center items-center w-full mx-auto js-pagination';
      list.dataset.isMoreResults = String(page < pages);
      list.dataset.paginationStyle = 'traditional';

      const href = (n) => this.localUrl(term, n);

      const numbered = (n) => {
        const item = document.createElement('li');
        item.className = 'pagination__item';

        if (n === page) {
          const current = document.createElement('span');
          current.className = 'pagination__page-current pagination__button font-bold block leading-none';
          current.setAttribute('aria-current', 'page');
          current.textContent = String(n);
          item.appendChild(current);
        } else {
          const link = document.createElement('a');
          link.className = 'pagination__page-link pagination__button text-current block leading-none';
          link.href = href(n);
          link.textContent = String(n);
          item.appendChild(link);
        }
        return item;
      };

      const arrow = (n, direction, label) => {
        const item = document.createElement('li');
        item.className = 'pagination__item pagination__item--arrow';
        const link = document.createElement('a');
        link.className = `pagination__arrow pagination__button pagination__arrow--${direction} `
          + 'text-current flex items-center justify-center';
        link.href = href(n);
        link.setAttribute('aria-label', label);
        const glyph = document.createElement('span');
        glyph.setAttribute('aria-hidden', 'true');
        glyph.textContent = direction === 'prev' ? '<' : '>';
        link.appendChild(glyph);
        item.appendChild(link);
        return item;
      };

      // Same shape the theme renders: first, prev, the pages either side of the
      // current one, next, last.
      if (page > 1) list.appendChild(numbered(1));
      if (page > 1) list.appendChild(arrow(page - 1, 'prev', 'Föregående'));

      for (let n = Math.max(1, page - 1); n <= Math.min(pages, page + 1); n += 1) {
        if (n === 1 && page > 1) continue;
        if (n === pages && page < pages) continue;
        list.appendChild(numbered(n));
      }

      if (page < pages) list.appendChild(arrow(page + 1, 'next', 'Nästa'));
      if (page < pages) list.appendChild(numbered(pages));

      return list;
    }

    /**
     * Rewrites the result count, reusing the noun the server already rendered
     * so this does not need a translation of its own.
     * @param {number} total - How many products matched.
     */
    setLocalCount(total) {
      const el = document.querySelector('.products-toolbar__count');
      if (!el) return;

      if (this.countTemplate == null) {
        // "613 produkter" -> "{n} produkter"
        this.countTemplate = el.textContent.trim().replace(/^[\d\s .,]+/, '{n} ');
      }
      el.textContent = this.countTemplate.replace('{n}', String(total));

      // The drawer's "Visa X resultat" button, which the search now sits above.
      const button = document.querySelector('facet-filters .js-close-drawer-mob');
      if (button) {
        if (this.buttonTemplate == null) this.buttonTemplate = button.textContent.trim().replace(/\d[\d\s .,]*/, '{n} ');
        button.textContent = this.buttonTemplate.replace('{n} ', `${total} `);
      }
    }

    /**
     * Puts the unsearched filter panel back when clearing the search.
     *
     * A collection that filters natively arrives with a panel; the search
     * replaced it with one describing the search results, so clearing has to
     * restore the original rather than drop it. Dropping it is what the page did
     * before - clearing the box left the collection with no filters at all until
     * a reload, measured on the live theme as 10 facets going to 0.
     *
     * An oversized collection has no pristine panel, so the swapped-in one is
     * removed exactly as it always was.
     */
    restoreFilters() {
      const current = this.grid.querySelector('.main-products-grid__filters');

      if (!this.pristine || !this.pristine.filters) {
        current?.remove();
        return;
      }

      const tmpl = document.createElement('template');
      tmpl.innerHTML = this.pristine.filters;
      const restored = tmpl.content.querySelector('.main-products-grid__filters');
      if (!restored) {
        current?.remove();
        return;
      }

      if (current) {
        current.replaceWith(restored);
      } else {
        this.grid.insertAdjacentElement('afterbegin', restored);
      }
    }
  }

  customElements.define('collection-search-scope', CollectionSearchScope);
}
